'use client';
import { createContext, ReactNode, Dispatch, useEffect, useRef } from 'react';

import { BrokerAction } from '@/components/context/ContextBroker/ContextBroker';
import { useContextBroker } from '@/components/context/ContextBroker/useContextBroker';

import { AddToWishListBFFResponse } from '@/types/Wishlist';
import { noop } from '@/util/misc';
import { CACHE_TIME } from '@/const/cache';
import { ProductCardData } from '@/types/Product';

export type WishlistItem = {
  wishlistItemId: string;
  sku: string;
  createdAt: string;
};

export type WishlistMap = { [sku: string]: boolean };

export type WishlistContextValue = {
  wishlist: WishlistItem[];
  wishlistAsMap: WishlistMap;
  itemCount: number;
  tempWishlist: WishlistItem[];
  tempWishlistAsMap: WishlistMap;
};

export type WishlistBrokerActions = {
  addToWishlist: (productSku: string) => Promise<void>;
  removeFromWishlist: (wishlistItemId: string) => Promise<void>;
  refreshWishlist: () => Promise<void>;
  addToTempWishlist: (productSku: string) => void;
  removeFromTempWishlist: (productSku: string) => void;
};

type WishlistBrokeredValue = {
  value: WishlistContextValue;
  actions: WishlistBrokerActions;
};

const TEMP_WISHLIST_KEY = 'TEMP_WISHLIST';

export const INITIAL_WISHLIST_VALUE = {
  wishlist: [],
  wishlistAsMap: {},
  itemCount: 0,
  tempWishlist: [],
  tempWishlistAsMap: {}
};

export const WishlistContext = createContext<WishlistBrokeredValue>({
  value: INITIAL_WISHLIST_VALUE,
  actions: {
    addToWishlist: noop,
    removeFromWishlist: noop,
    refreshWishlist: noop,
    addToTempWishlist: noop,
    removeFromTempWishlist: noop
  }
});

type WishlistProviderProps = {
  children: ReactNode;

  dispatch: Dispatch<BrokerAction>;
};

function WishlistProvider(props: WishlistProviderProps) {
  const { children, dispatch } = props;
  const brokerValue = useContextBroker();

  // In-flight guard for refreshWishlist to dedupe concurrent requests
  const wishlistRefreshInFlight = useRef<Promise<void> | null>(null);

  // Initialize temp wishlist from localStorage
  useEffect(() => {
    const storedTempWishlist = localStorage.getItem(TEMP_WISHLIST_KEY);
    if (storedTempWishlist) {
      try {
        const parsedItems = JSON.parse(storedTempWishlist);
        // Handle both old format (array of SKUs) and new format (array of WishlistItems)
        const parsedTempWishlist = Array.isArray(parsedItems)
          ? parsedItems.map((item) =>
              typeof item === 'string'
                ? {
                    wishlistItemId: '',
                    sku: item,
                    createdAt: new Date().toISOString()
                  }
                : item
            )
          : [];

        dispatch({
          type: 'WISHLIST_SET_TEMP',
          tempWishlist: parsedTempWishlist
        });
      } catch (e) {
        console.error('Error parsing temp wishlist:', e);
      }
    }
  }, []); // Remove dispatch from dependencies since it's stable

  const decoratedValue: WishlistBrokeredValue = {
    value: {
      ...brokerValue.wishlist,
      tempWishlist: brokerValue.wishlist.tempWishlist || [],
      tempWishlistAsMap: brokerValue.wishlist.tempWishlistAsMap || {}
    },
    actions: {
      /**
       * Adds the passed in SKU to the user's wishlist.
       */
      addToWishlist: async (productSku: string): Promise<void> => {
        if (!brokerValue.user) {
          throw Error('User is not logged in!');
        }

        const response = await fetch('/api/wishlist', {
          method: 'POST',
          body: JSON.stringify({
            sku: productSku,
            userId: brokerValue.user.id
          }),
          headers: {
            'content-type': 'application/json'
          }
        });

        let json;
        try {
          json = (await response.json()) as {
            success: boolean;
            data: AddToWishListBFFResponse['data']['createdEntry'];
          };
        } catch (e) {
          console.error(e);
        }

        if (json?.data) {
          const newWishlistItem: WishlistItem = {
            wishlistItemId: json.data.wishlistId,
            sku: json.data.sku,
            createdAt: json.data.created_date
          };

          dispatch({
            type: 'WISHLIST_ADD_ITEM',
            wishlistEntry: newWishlistItem
          });
        }
      },

      removeFromWishlist: async (wishlistItemId: string): Promise<void> => {
        if (!brokerValue.user) {
          throw Error('User is not logged in!');
        }

        await fetch('/api/wishlist', {
          method: 'DELETE',
          body: JSON.stringify({
            wishlistItemId,
            userId: brokerValue.user.id
          }),
          headers: {
            'content-type': 'application/json'
          }
        });

        dispatch({ type: 'WISHLIST_REMOVE_ITEM', wishlistItemId });
      },

      refreshWishlist: async (): Promise<void> => {
        // If a refresh is already in-flight, return the same promise to coalesce callers
        if (wishlistRefreshInFlight.current) {
          return wishlistRefreshInFlight.current;
        }

        wishlistRefreshInFlight.current = (async () => {
          const response = await fetch('/api/wishlist', {
            method: 'GET',
            next: {
              revalidate: CACHE_TIME.NO_CACHE
            }
          });

          let json;
          try {
            json = (await response.json()) as {
              success: boolean;
              data: WishlistContextValue;
            };
          } catch (e) {
            console.error(e);
          }

          if (json?.data) {
            dispatch({
              type: 'WISHLIST_REFRESH',
              newContextValue: json.data
            });
          }
        })();

        try {
          await wishlistRefreshInFlight.current;
        } finally {
          wishlistRefreshInFlight.current = null;
        }
      },

      addToTempWishlist: (productSku: string) => {
        const wishlistEntry = {
          wishlistItemId: '',
          sku: productSku,
          createdAt: new Date().toISOString()
        };
        dispatch({
          type: 'WISHLIST_ADD_ITEM_TEMP',
          wishlistEntry
        });
        const updatedTempWishlist = [
          ...(brokerValue.wishlist.tempWishlist || []),
          wishlistEntry
        ];
        localStorage.setItem(
          TEMP_WISHLIST_KEY,
          JSON.stringify(updatedTempWishlist)
        );
      },

      removeFromTempWishlist: (productSku: string) => {
        dispatch({
          type: 'WISHLIST_REMOVE_ITEM_TEMP',
          productSku: productSku
        });
        const updatedTempWishlist = (
          brokerValue.wishlist.tempWishlist || []
        ).filter((item) => item.sku !== productSku);
        localStorage.setItem(
          TEMP_WISHLIST_KEY,
          JSON.stringify(updatedTempWishlist)
        );
      }
    }
  };

  // Centralized initial load: when user id becomes available, refresh wishlist once
  useEffect(() => {
    if (brokerValue.user?.id) {
      decoratedValue.actions.refreshWishlist();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brokerValue.user?.id]);

  return (
    <WishlistContext.Provider value={decoratedValue}>
      {children}
    </WishlistContext.Provider>
  );
}

export default WishlistProvider;
